import React, { FC } from 'react';
import { Button } from '@mui/material';
import { useSelector } from 'react-redux';

import { IEmployeeBalanceDTO } from 'src/api/dtos';
import { AbsenceBalanceSummary } from 'src/features/absences/future-balance';
import { ClockDottedIcon, FeelingHappyIcon, UserXIcon } from 'src/shared/components';
import { ComponentWithFade } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';
import { RootState } from 'src/store/types';

import { AbsenceRequestCard } from './AbsenceRequestCard';
import './AbsenceRequestForm.styles.scss';

interface Props {
  balance: IEmployeeBalanceDTO | undefined;
  cancelling: boolean;
  onCancelling: (notificationId: number) => void;
  onNavigate: () => void;
  onViewDetails?: () => void;
}

export const AbsenceRequestSuccess: FC<Props> = ({ balance, cancelling, onNavigate, onCancelling, onViewDetails }) => {
  const { t } = useTranslate();
  const { settings } = useSelector((state: RootState) => state.user);

  const { item: newAbsence } = useSelector((state: RootState) => state.absences);
  const hasEmptyBalance = !balance?.currentBalanceInSeconds && !balance?.expectedBalanceInSeconds;

  if (!newAbsence) {
    return null;
  }

  return (
    <ComponentWithFade show mountDuration={200}>
      <FeelingHappyIcon />
      <div className="mt-3 mb-4">
        <h4>{t('absences.congratulations')}</h4>
        <p className="mt-1 text-muted">{t('absences.yourAbsenceRequestHasBeenSubmitted')}</p>
        <div className="d-flex align-items-center mt-3">
          <Button
            id="submit-another-request"
            className="me-2"
            startIcon={<UserXIcon />}
            size="small"
            variant="contained"
            color="info"
            onClick={onNavigate}
          >
            {t('absences.submitAnotherRequest')}
          </Button>
          {onViewDetails && (
            <Button
              id="view-all-absence-details"
              startIcon={<ClockDottedIcon />}
              variant="contained"
              color="info"
              size="small"
              onClick={onViewDetails}
            >
              {t('absences.viewAllAbsenceDetails')}
            </Button>
          )}
        </div>
      </div>
      <div className="my-4 pt-3">
        <h5>{t('absences.detailsOfYourRecentRequest')}</h5>
        <AbsenceRequestCard className="my-3" absence={newAbsence} cancelling={cancelling} onCancelling={onCancelling} />
      </div>
      {balance && !hasEmptyBalance && (
        <div className="my-4">
          <h5>{t('absences.balancesSummary')}</h5>

          <AbsenceBalanceSummary
            className="mt-3"
            balance={balance}
            shouldShowError={!!settings?.isFutureBalanceViolationBlockEnabled}
            shouldDisplayAsDays={!!settings?.showAccrualBalanceInDays}
            isFutureBalanceCheckEnabled={!!settings?.isFutureBalanceCheckEnabled}
          />
        </div>
      )}
    </ComponentWithFade>
  );
};
